<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { Button, Description, Field, InlineAlert, Input, Label, Radio, RadioField, RadioGroup, Select } from 'catalyst-vue'
import type { CollectionMode } from '../../lib/api-client'
import { useCloudLibraryStore } from '../../stores/cloud-library'
import { useLibraryEntriesStore } from '../../stores/library-entries'
import { addToCollection, createCollection } from '../../lib/collection-service'
import LibraryOverlay from './library-overlay.vue'

/**
 * 「加入集合」：把選取中的作品建成新集合，或加進既有集合。
 * 已在別的集合的作品會移過來；未註冊的作品被擋下並在完成後說明，其餘照常送出。
 */
const props = defineProps<{ open: boolean }>()
const emit = defineEmits<{ close: []; done: [message: string] }>()
const cloud = useCloudLibraryStore()
const store = useLibraryEntriesStore()

const NEW = 'new'
const EXISTING = 'existing'
// 依建立時間新到舊，與書架的預設排序一致；集合多的時候一長串 radio 很難用（2026-09 回報），
// 因此只有兩個選項，選「加入現有集合」才出現一個下拉選單。
const collections = computed(() => [...cloud.collections].sort((a, b) => b.createdAt - a.createdAt))
/** 只有作品格會被加進集合；選取裡的集合格排除掉。 */
const comics = computed(() => store.selectedEntries.map(entry => entry.local ?? entry.comic))
const selectedCount = computed(() => comics.value.length)
const skippedCollections = computed(() => store.skippedCollectionCount)
/** 建立新集合，或加入現有集合。 */
const target = ref<string>(NEW)
const existingId = ref('')
const title = ref('')
const mode = ref<CollectionMode>('series')
const busy = ref(false)
const error = ref('')

watch(() => props.open, isOpen => {
  if (!isOpen) return
  target.value = NEW
  title.value = ''
  mode.value = 'series'
  existingId.value = collections.value[0]?.id ?? ''
  error.value = ''
})

const canApply = computed(() => !!selectedCount.value && (target.value === NEW ? !!title.value.trim() : !!existingId.value))

async function apply() {
  if (!canApply.value || busy.value) return
  busy.value = true
  error.value = ''
  try {
    const result = target.value === NEW
      ? await createCollection(title.value, comics.value, undefined, mode.value)
      : await addToCollection(existingId.value, comics.value)
    await cloud.refresh(true)
    emit('done', result.message)
    emit('close')
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : '集合尚未建立'
  } finally { busy.value = false }
}
</script>

<template>
  <LibraryOverlay
    :open="open"
    title="加入集合"
    :description="`把已選取的 ${selectedCount} 本作品放進一個集合。集合在書架上只佔一格，點進去才看到每一本。${skippedCollections ? `（選取裡的 ${skippedCollections} 個集合不在範圍內）` : ''}`"
    :pending="busy"
    @close="emit('close')"
  >
    <InlineAlert v-if="error" tone="error" class="mb-3">{{ error }}</InlineAlert>
    <RadioGroup v-model="target">
      <RadioField>
        <Radio :value="NEW" color="primary" />
        <Label>建立新集合</Label>
        <Description>成員會依標題自然排序（第 2 卷在第 10 卷之前）。</Description>
      </RadioField>
      <RadioField :disabled="!collections.length">
        <Radio :value="EXISTING" color="primary" :disabled="!collections.length" />
        <Label>加入現有集合</Label>
        <Description>{{ collections.length ? '新成員接在既有成員之後；已在別的集合的作品會移過來。' : '目前還沒有任何集合。' }}</Description>
      </RadioField>
    </RadioGroup>
    <Field v-if="target === EXISTING" class="mt-4">
      <Label>選擇集合</Label>
      <Select v-model="existingId" :disabled="busy || !collections.length">
        <option v-for="collection in collections" :key="collection.id" :value="collection.id">
          {{ collection.title }}（{{ collection.memberCount }} 本{{ collection.mode === 'merged' ? '．合併冊' : '' }}）
        </option>
      </Select>
    </Field>
    <Field v-if="target === NEW" class="mt-4">
      <Label>集合標題</Label>
      <Input v-model="title" placeholder="例如：獵人" :disabled="busy" @keydown.enter.prevent="apply" />
    </Field>
    <Field v-if="target === NEW" class="mt-4">
      <Label>模式</Label>
      <RadioGroup v-model="mode" :disabled="busy">
        <RadioField>
          <Radio value="series" color="primary" />
          <Label>集合</Label>
          <Description>點進去列出成員，每本各自閱讀。</Description>
        </RadioField>
        <RadioField>
          <Radio value="merged" color="primary" />
          <Label>合併冊</Label>
          <Description>成員依順序串成一本連續閱讀，適合尚未集結成單行本的分話。</Description>
        </RadioField>
      </RadioGroup>
    </Field>
    <template #actions>
      <Button outline :disabled="busy" @click="emit('close')">取消</Button>
      <Button color="primary" :disabled="busy || !canApply" @click="apply">
        {{ busy ? '正在加入…' : `${target === NEW ? '建立集合' : '加入集合'}（${selectedCount} 本）` }}
      </Button>
    </template>
  </LibraryOverlay>
</template>
